// src/components/ecommerce/OrderConfirmation.jsx
import React from 'react';
import { CheckCircle, Copy, Download, Mail, MessageCircle, Phone } from 'lucide-react';
import toast from 'react-hot-toast';

export const OrderConfirmation = ({ createdOrder, storeSettings }) => {
  const copyOrderNumber = () => {
    navigator.clipboard.writeText(createdOrder.orderNumber);
    toast.success('Order number copied!');
  };

  const getPaymentInstructions = () => {
    if (createdOrder.payment.method === 'payfast') {
      return 'You will be redirected to PayFast to complete your payment.';
    } else if (createdOrder.payment.method === 'eft') {
      return 'Please transfer the payment using the bank details provided and email proof of payment.';
    } else if (createdOrder.payment.method === 'cod') {
      return 'Payment will be collected upon delivery.';
    }
    return '';
  };

  const getStatusMessage = () => {
    if (createdOrder.payment.method === 'payfast') {
      return 'Awaiting payment confirmation';
    } else if (createdOrder.payment.method === 'eft') {
      return 'Awaiting payment';
    } else if (createdOrder.payment.method === 'cod') {
      return 'Order confirmed';
    }
    return 'Order placed';
  };

  return (
    <div className="text-center space-y-6">
      {/* Success Icon */}
      <div className="flex justify-center">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
          <CheckCircle className="h-12 w-12 text-green-600" />
        </div>
      </div>

      {/* Success Message */}
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Order Placed Successfully!</h3>
        <p className="text-gray-600">{getStatusMessage()}</p>
      </div>

      {/* Order Details */}
      <div className="bg-gray-50 rounded-lg p-6">
        <div className="flex items-center justify-center gap-2 mb-4">
          <span className="text-lg font-semibold text-gray-900">Order #{createdOrder.orderNumber}</span>
          <button
            onClick={copyOrderNumber}
            className="p-1 hover:bg-gray-200 rounded transition-colors"
          >
            <Copy className="h-4 w-4 text-gray-600" />
          </button>
        </div>
        
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Total Amount:</span>
            <p className="font-semibold">R{createdOrder.summary.grandTotal.toFixed(2)}</p>
          </div>
          <div>
            <span className="text-gray-600">Payment Method:</span>
            <p className="font-semibold capitalize">{createdOrder.payment.method}</p>
          </div>
          <div>
            <span className="text-gray-600">Delivery:</span>
            <p className="font-semibold">{createdOrder.shipping.deliveryZone?.name}</p>
          </div>
          <div>
            <span className="text-gray-600">Delivery Time:</span>
            <p className="font-semibold">{createdOrder.shipping.deliveryZone?.deliveryTime}</p>
          </div>
        </div>
      </div>

      {/* Payment Instructions */}
      <div className="bg-blue-50 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-2">Next Steps:</h4>
        <p className="text-sm text-blue-800">{getPaymentInstructions()}</p>
      </div>

      {/* Contact Information */}
      <div className="border-t border-gray-200 pt-6">
        <h4 className="font-medium text-gray-900 mb-4">Need Help?</h4>
        <div className="flex justify-center gap-4">
          {storeSettings.whatsappNumber && (
            <a
              href={`https://wa.me/${storeSettings.whatsappNumber.replace(/\D/g, '')}?text=Hi, I need help with order ${createdOrder.orderNumber}`}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <MessageCircle className="h-4 w-4" />
              WhatsApp
            </a> 
          )}
          
          {storeSettings.email && (
            <a
              href={`mailto:${storeSettings.email}?subject=Order ${createdOrder.orderNumber}`}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Mail className="h-4 w-4" />
              Email
            </a>
          )}
        </div>
      </div>
    </div>
  );
};